import React, { useState, useEffect } from 'react';
import { UserPlus, Trash2, ClipboardList, MessageSquareText } from 'lucide-react';
import ManageMemberDietaryFlags from './ManageMemberDietaryFlags';
import { formatDateByPref } from '../utils/formatting';

// (8 Sep 2026) Members tab — GA-facing list/add/remove for calendar_members.
// The backend (memberRoutes.js) has existed and been live since the Aug
// 2026 modularization, gated on access.isOwner — this is genuinely its
// first UI, same situation appoint-ga/raise-cap were in before their
// screen got built.
//
// Built as the foundation for GA/RA proxy-entry (dietary flags, poll
// responses on behalf of a member) — see the design agreed 8 Sep. Pass 2
// was just the list; Pass 3 added the "Manage" dietary-flags action per
// member. Pass 4 (9 Sep) adds a second action here: "Answer poll" — see
// handleAnswerPoll below for the resolution logic (real usage starts from
// a member naming themselves on WhatsApp, e.g. "add my canapé offering
// for the next meeting" — not from the GA picking an event first, so this
// resolves "which poll is open right now" itself via the new
// /calendars/:calendarId/open-polls endpoint, only asking the GA to
// choose when there's genuinely more than one open poll at once).
// (25 Sep 2026) Bulk add — pull email addresses out of whatever was pasted
// (one per line, commas, semicolons, an Excel column, "Name <email>" lines).
// Names and any other text are ignored; each member sets their own name at
// first sign-in. Same shape rule as the backend's isSafeEmail, so the
// preview agrees with what the server will accept.
const isSafeEmail = (s) =>
  typeof s === 'string' &&
  s.length <= 254 &&
  /^[^\s@,()<>";\\]+@[^\s@,()<>";\\]+\.[^\s@,()<>";\\]+$/.test(s);

const extractEmails = (text) => {
  const tokens = (text || '').match(/[^\s,;<>()"'[\]]+@[^\s,;<>()"'[\]]+/g) || [];
  return tokens.map((t) => t.replace(/^mailto:/i, '').replace(/[.:]+$/, '').toLowerCase());
};

const MembersTab = ({ currentCalendar, apiBaseUrl, authToken, onStatusMessage, onAnswerPollForMember, userTier }) => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [confirmingRemoveEmail, setConfirmingRemoveEmail] = useState(null);
  const [managingMember, setManagingMember] = useState(null);
  // (9 Sep 2026) Pass 4 — "Answer poll" flow state. answeringPollFor holds
  // the id of the member currently being resolved (disables that row's
  // button while the open-polls fetch is in flight); pollChoice holds
  // { member, options } only in the rare case of >1 open poll, driving
  // the small inline chooser modal below.
  const [answeringPollFor, setAnsweringPollFor] = useState(null);
  const [pollChoice, setPollChoice] = useState(null);
  // (19 Sep 2026) The calendar's member cap, shown as "N of M members".
  // Keyed by calendar id so a value fetched for one calendar is never shown
  // against another while a switch is in flight. null = not loaded / not
  // available - the counter is simply omitted (it is informational only;
  // the server enforces the cap regardless).
  const [limitInfo, setLimitInfo] = useState(null);
  // (25 Sep 2026) Bulk add: bulkOpen shows the paste box; bulkPreview holds
  // the checked list before saving; bulkResults the outcome after saving.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const refetch = () => {
    if (!currentCalendar?.id) return;
    setIsLoading(true);
    fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/members`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then(async (res) => {
        if (res.ok) return res.json();
        // (9 Sep 2026) Previously fell straight to setMembers([]) on any
        // non-OK response, which is indistinguishable in the UI from a
        // genuinely empty member list — that's exactly what masked the
        // isOwner/isOwnerAccount 403 bug fixed today in memberRoutes.js.
        // Surface the actual error instead.
        const body = await res.json().catch(() => ({}));
        onStatusMessage(body.error || 'Failed to load members', 'error');
        return [];
      })
      .then((data) => setMembers(data))
      .catch(() => {
        onStatusMessage('Failed to load members', 'error');
        setMembers([]);
      })
      .finally(() => setIsLoading(false));
  };

  const refetchLimit = () => {
    const calendarId = currentCalendar?.id;
    if (!calendarId) return;
    fetch(`${apiBaseUrl}/api/calendars/${calendarId}/members/limit`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        setLimitInfo(data && Number.isInteger(data.maxMembers)
          ? { calendarId, maxMembers: data.maxMembers }
          : null);
      })
      .catch(() => setLimitInfo(null));
  };

  useEffect(() => {
    refetch();
    refetchLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCalendar?.id, apiBaseUrl, authToken]);

  const handleAddMember = async () => {
    const email = newEmail.trim();
    if (!email || !email.includes('@')) {
      onStatusMessage('Please enter a valid email', 'error');
      return;
    }
    setIsAdding(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        // (21 Sep 2026) The message is now longer (it says whether the new member was emailed), so
        // it stays up long enough to read; if the email could not be sent it stays up until dismissed.
        onStatusMessage(data.message || 'Member added', 'success', data.emailSent === false ? 9000 : 6000);
        setNewEmail('');
        refetch();
      } else {
        onStatusMessage(data.error || 'Failed to add member', 'error', 9000);
      }
    } catch (err) {
      onStatusMessage('Failed to add member', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  // (25 Sep 2026) Bulk add — check the pasted list against the members
  // already loaded. The server re-checks everything on save; this preview is
  // only so the GA can see what will happen before anything is added.
  const handleBulkCheck = () => {
    const found = extractEmails(bulkText);
    const memberEmails = new Set(members.map((m) => (m.email || '').toLowerCase()));
    // (2 Oct 2026) A member's secondary contact shares that membership, so it is not new.
    const primaryBySecondary = new Map(
      members
        .filter((m) => m.secondary_contact_email)
        .map((m) => [m.secondary_contact_email.toLowerCase(), m.preferred_name || m.email])
    );
    const seen = new Set();
    const items = [];
    let repeats = 0;
    for (const e of found) {
      if (seen.has(e)) { repeats += 1; continue; }
      seen.add(e);
      items.push({
        email: e,
        status: !isSafeEmail(e) ? 'invalid'
          : memberEmails.has(e) ? 'member'
          : primaryBySecondary.has(e) ? 'secondary'
          : 'new',
        primaryName: primaryBySecondary.get(e) || null,
      });
    }
    if (items.length === 0) {
      onStatusMessage('No email addresses found in the list', 'error');
      return;
    }
    setBulkPreview({ items, repeats });
  };

  const handleBulkAdd = async () => {
    if (!bulkPreview) return;
    const emails = bulkPreview.items.filter((i) => i.status === 'new').map((i) => i.email);
    if (emails.length === 0) return;
    setBulkSaving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/members/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emails })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const results = data.results || [];
        const failedMail = results.filter((r) => r.emailSent === false).length;
        setBulkResults(results);
        setBulkPreview(null);
        onStatusMessage(
          `${data.added} ${data.added === 1 ? 'member' : 'members'} added.`
            + (failedMail ? ` ${failedMail} email${failedMail === 1 ? '' : 's'} could not be sent — see the list.` : ''),
          'success',
          failedMail ? 9000 : 6000
        );
        refetch();
      } else {
        onStatusMessage(data.error || 'Failed to add members', 'error', 9000);
      }
    } catch (err) {
      onStatusMessage('Failed to add members', 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  const closeBulk = () => {
    setBulkOpen(false);
    setBulkText('');
    setBulkPreview(null);
    setBulkResults(null);
  };

  // (8 Sep 2026) Replaced window.confirm — same reasoning as the fix in
  // ManageMemberDietaryFlags.jsx: it's un-stylable and looked out of place
  // next to the app's own confirmation modals.
  const handleRemoveMemberConfirmed = async (email) => {
    setConfirmingRemoveEmail(null);
    setRemovingEmail(email);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/members/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Member removed', 'success');
        refetch();
      } else {
        onStatusMessage(data.error || 'Failed to remove member', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to remove member', 'error');
    } finally {
      setRemovingEmail(null);
    }
  };

  // (9 Sep 2026) Resolves "which poll" without making the GA pick an
  // event first — matches how these requests actually arrive (a member
  // naming themselves on WhatsApp, not an event). Zero options: just
  // says so. Exactly one: goes straight into proxy mode. More than one
  // (rare — the schema doesn't forbid it): shows a tiny inline chooser
  // rather than guessing.
  // (14 Sep 2026) /open-polls now also returns CLOSED polls on events
  // that haven't happened yet (late-entry override — see pollsService.js/
  // pollRoutes.js), each tagged with its own status, so an event's
  // options here are no longer always exactly one open poll. pollId is
  // now passed through alongside eventUid — PollPage.jsx needs it to
  // fetch the exact poll chosen rather than re-resolving "whatever's
  // open" for the event, which a closed option wouldn't match.
  const handleAnswerPoll = async (member) => {
    setAnsweringPollFor(member.id);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/open-polls`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const options = res.ok ? await res.json() : [];
      if (!res.ok || options.length === 0) {
        onStatusMessage('No poll available to answer right now for this calendar.', 'info');
        return;
      }
      if (options.length === 1) {
        onAnswerPollForMember({
          calendarId: currentCalendar.id,
          memberId: member.id,
          eventUid: options[0].eventUid,
          pollId: options[0].pollId,
        });
        return;
      }
      // (26 Sep 2026, H5) Fixed order: by event date, then Phase 1 before
      // Phase 2 for the same event — the server's order varied.
      const phaseRank = (p) => (p === 'confirm' ? 1 : 0);
      const sorted = [...options].sort((a, b) =>
        (a.eventDate || '').localeCompare(b.eventDate || '') || phaseRank(a.phase) - phaseRank(b.phase));
      setPollChoice({ member, options: sorted });
    } catch (err) {
      onStatusMessage('Failed to check for open polls', 'error');
    } finally {
      setAnsweringPollFor(null);
    }
  };

  const handleChoosePoll = (opt) => {
    if (!pollChoice) return;
    onAnswerPollForMember({
      calendarId: currentCalendar.id,
      memberId: pollChoice.member.id,
      eventUid: opt.eventUid,
      pollId: opt.pollId,
    });
    setPollChoice(null);
  };

  if (!currentCalendar) {
    return <p className="text-sm text-gray-500">No calendar selected.</p>;
  }

  return (
    <div>
      {/* (25 Sep 2026) One heading naming the calendar, in place of "Members"
          over a small grey calendar-name line. */}
      <h3 className="text-lg font-semibold mb-4">Members of the {currentCalendar.name} calendar</h3>

      <div className="border border-gray-200 rounded-lg p-3 space-y-2 mb-4">
        <p className="text-sm font-medium text-gray-700">Add a member</p>
        <input
          type="email"
          autoComplete="off"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddMember(); }}
          placeholder="member@example.com"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={handleAddMember}
          disabled={isAdding}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          <UserPlus className="w-4 h-4" />
          {isAdding ? 'Adding…' : 'Add Member'}
        </button>

        {/* (25 Sep 2026) Bulk add: paste → Check list → preview → Add. */}
        {!bulkOpen ? (
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="w-full text-sm text-blue-700 hover:underline pt-1"
          >
            Add several at once
          </button>
        ) : (
          <div className="border-t border-gray-200 pt-3 mt-1 space-y-2">
            <p className="text-sm font-medium text-gray-700">Add several at once</p>

            {bulkResults ? (
              <>
                <ul className="max-h-64 overflow-y-auto space-y-1 text-xs">
                  {bulkResults.map((r) => (
                    <li key={r.email} className="flex items-start justify-between gap-2">
                      <span className="break-all text-gray-700">{r.email}</span>
                      <span className={`shrink-0 ${
                        r.result === 'invalid' ? 'text-red-600'
                          : (r.result === 'already_member' || r.result === 'secondary_contact') ? 'text-gray-500'
                          : r.emailSent === false ? 'text-amber-700' : 'text-green-700'
                      }`}>
                        {r.result === 'added_active' && 'Added'}
                        {r.result === 'added_pending' && 'Added (pending sign-in)'}
                        {r.result === 'already_member' && 'Already a member'}
                        {r.result === 'secondary_contact' && `Secondary contact of ${r.primaryName}`}
                        {r.result === 'invalid' && 'Not a valid address'}
                        {r.emailSent === false && ' — email not sent, please tell them'}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={closeBulk}
                  className="w-full px-3 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
                >
                  Done
                </button>
              </>
            ) : bulkPreview ? (() => {
              const newCount = bulkPreview.items.filter((i) => i.status === 'new').length;
              const hasLimit = limitInfo && limitInfo.calendarId === currentCalendar.id;
              const after = members.length + newCount;
              const overLimit = hasLimit && after > limitInfo.maxMembers;
              const left = hasLimit ? Math.max(0, limitInfo.maxMembers - members.length) : null;
              return (
                <>
                  <ul className="max-h-64 overflow-y-auto space-y-1 text-xs">
                    {bulkPreview.items.map((i) => (
                      <li key={i.email} className="flex items-start justify-between gap-2">
                        <span className="break-all text-gray-700">{i.email}</span>
                        <span className={`shrink-0 ${
                          i.status === 'new' ? 'text-green-700' : (i.status === 'member' || i.status === 'secondary') ? 'text-gray-500' : 'text-red-600'
                        }`}>
                          {i.status === 'new' && 'New'}
                          {i.status === 'member' && 'Already a member'}
                          {i.status === 'secondary' && `Secondary contact of ${i.primaryName}`}
                          {i.status === 'invalid' && 'Not a valid address'}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className={`text-xs ${overLimit ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                    {newCount} new
                    {bulkPreview.repeats > 0 && ` · ${bulkPreview.repeats} repeated ${bulkPreview.repeats === 1 ? 'address' : 'addresses'} counted once`}
                    {hasLimit && ` · ${after} of ${limitInfo.maxMembers} members after adding`}
                  </p>
                  {overLimit && (
                    <p className="text-xs text-red-600">
                      Only {left} {left === 1 ? 'place is' : 'places are'} left on this calendar. Remove {after - limitInfo.maxMembers} from the list to continue.
                    </p>
                  )}
                  {newCount === 0 && !overLimit && (
                    <p className="text-xs text-gray-600">Nobody new to add from this list.</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setBulkPreview(null)}
                      disabled={bulkSaving}
                      className="flex-1 px-3 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Edit list
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkAdd}
                      disabled={bulkSaving || newCount === 0 || overLimit}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      <UserPlus className="w-4 h-4" />
                      {bulkSaving ? 'Adding…' : `Add ${newCount} ${newCount === 1 ? 'member' : 'members'}`}
                    </button>
                  </div>
                </>
              );
            })() : (
              <>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={6}
                  placeholder="Paste email addresses: one per line, or separated by commas or semicolons"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeBulk}
                    className="flex-1 px-3 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkCheck}
                    disabled={!bulkText.trim()}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    Check list
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">Current members</p>
          {!isLoading && limitInfo && limitInfo.calendarId === currentCalendar.id && (
            <span
              className={`text-xs ${members.length >= limitInfo.maxMembers ? 'text-red-600 font-semibold' : 'text-gray-500'}`}
            >
              {members.length} of {limitInfo.maxMembers} members
              {/* (2 Oct 2026) Was a hover tooltip; now visible, and only when it applies. */}
              {(() => {
                const pending = members.filter((m) => m.status !== 'active').length;
                return pending > 0 ? ` (${pending} not yet signed in)` : '';
              })()}
            </span>
          )}
        </div>
        {isLoading ? (
          <p className="text-xs text-gray-400">Loading…</p>
        ) : members.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No members yet.</p>
        ) : (
          <>
          {/* (2 Oct 2026) What the row icons do, in words, for phones and iPads
              where hover captions never appear (e.g. a stand-in using them for
              the first time). Shown once above the list rather than on every row. */}
          {members.some((m) => m.status === 'active') && (
            <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-3 pb-2 border-b border-gray-100">
              <span className="flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> Dietary flags</span>
              <span className="flex items-center gap-1"><MessageSquareText className="w-3.5 h-3.5" /> Answer the poll for them</span>
              <span className="flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</span>
            </p>
          )}
          <ul className="space-y-2">
            {members.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 text-sm">
                {/* (24 Sep 2026) Badges pinned to one column: the name/email block
                    takes all the spare width (flex-1), so every badge sits against the
                    icons rather than just after its own email; the icons get a fixed
                    width (room for all three) so a pending row, with only the bin, keeps
                    its badge in the same column too. */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {/* (24 Sep 2026) Preferred name first, email beneath in small grey
                      text, matching how members are named everywhere else. No hover:
                      a tooltip can't be reached on a phone or iPad. Members without a
                      name (pending, or never set one) show the email alone, as before. */}
                  {m.preferred_name ? (
                    <span className="min-w-0 flex-1 flex flex-col">
                      <span className="text-gray-800 truncate">{m.preferred_name}</span>
                      {/* (25 Sep 2026) Email wraps instead of truncating, so the whole
                          address is visible and can be selected and copied. */}
                      <span className="text-xs text-gray-500 break-all">{m.email}</span>
                      {/* (2 Oct 2026) A couple's partner, who shares this membership. */}
                      {m.secondary_contact_email && (
                        <span className="text-xs text-gray-500 break-all">Secondary contact: {m.secondary_contact_email}</span>
                      )}
                    </span>
                  ) : (
                    <span className="min-w-0 flex-1 flex flex-col">
                      <span className="text-gray-700 break-all">{m.email}</span>
                      {m.secondary_contact_email && (
                        <span className="text-xs text-gray-500 break-all">Secondary contact: {m.secondary_contact_email}</span>
                      )}
                    </span>
                  )}
                  {/* (26 Sep 2026, H3) Each member's own email setting under the status,
                      so the GA can check that anyone on "None" is really in the
                      WhatsApp group. "None" in amber to catch the eye. */}
                  <span className="shrink-0 flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {m.status === 'active' ? 'Active' : 'Pending sign-in'}
                    </span>
                    {/* (7 Oct 2026, decision 14) Two settings, two labels: "Emails: all" used
                        to describe the poll emails only and hid that event emails were off. */}
                    <span className={`text-xs ${m.email_notifications === 'none' ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
                      {m.email_notifications === 'none'
                        ? 'Poll emails: none'
                        : m.email_notifications === 'attending'
                          ? 'Poll emails: attending only'
                          : 'Poll emails: all'}
                    </span>
                    <span className={`text-xs ${m.email_event_updates === false ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
                      {m.email_event_updates === false ? 'Event emails: off' : 'Event emails: on'}
                    </span>
                  </span>
                </div>
                <span className="flex items-center justify-end gap-2 shrink-0 w-16">
                  {m.status === 'active' && (
                    <>
                      <button
                        onClick={() => setManagingMember(m)}
                        className="text-gray-400 hover:text-blue-600 transition"
                        title="Manage dietary flags"
                        aria-label="Manage dietary flags"
                      >
                        <ClipboardList className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAnswerPoll(m)}
                        disabled={answeringPollFor === m.id}
                        className="text-gray-400 hover:text-blue-600 transition disabled:opacity-50"
                        title="Answer poll on their behalf"
                        aria-label="Answer poll on their behalf"
                      >
                        <MessageSquareText className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setConfirmingRemoveEmail(m.email)}
                    disabled={removingEmail === m.email}
                    className="text-gray-400 hover:text-red-600 transition disabled:opacity-50"
                    title="Remove member"
                        aria-label="Remove member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>

      {managingMember && (
        <ManageMemberDietaryFlags
          calendarId={currentCalendar.id}
          member={managingMember}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onStatusMessage={onStatusMessage}
          onClose={() => setManagingMember(null)}
        />
      )}

      {/* (9 Sep 2026) Pass 4 — shown only when handleAnswerPoll found more
          than one option for this calendar; the normal case (exactly
          one) skips this and goes straight into proxy mode.
          (14 Sep 2026) Options can now include closed polls (late-entry
          override) alongside open ones — each labelled distinctly so the
          GA knows they're overriding a closed poll, not editing a live
          one. */}
      {pollChoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Which poll?</h3>
            <p className="text-gray-600 mb-4">
              More than one poll is available. Which one is{' '}
              <span className="font-medium">{pollChoice.member.preferred_name || pollChoice.member.email}</span> answering?
            </p>
            <ul className="space-y-2 mb-4">
              {pollChoice.options.map((opt) => (
                <li key={opt.pollId}>
                  <button
                    onClick={() => handleChoosePoll(opt)}
                    className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <span className="text-sm font-medium text-gray-800">{opt.eventTitle}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {/* (26 Sep 2026, H5) Date in the GA's own format (was ISO), and the
                          app's standard phase names. */}
                      {formatDateByPref(opt.eventDate, userTier?.preferred_date_format)} · {opt.phase === 'confirm' ? 'Phase 2 (confirm)' : 'Phase 1 (offers)'}
                    </span>
                    {opt.status === 'closed' && (
                      <span className="block text-xs text-amber-700 font-medium mt-0.5">
                        Closed — late entry
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button
                onClick={() => setPollChoice(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmingRemoveEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Remove member?</h3>
            <p className="text-gray-600 mb-4">
              This will remove <span className="font-medium">{confirmingRemoveEmail}</span> from this calendar.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmingRemoveEmail(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveMemberConfirmed(confirmingRemoveEmail)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersTab;
